import React, { useState, useEffect } from 'react';
import { get } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const fmt = n => '₦' + Number(n||0).toLocaleString('en-NG');

export default function FinanceDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    get('/dashboard/finance').then(d => setData(d)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="spinner"></div>;
  if (!data) return <div className="empty-state"><h3>Unable to load dashboard</h3></div>;

  const StatTile = ({ label, value, sub, color, bg, alert }) => (
    <div style={{background:bg||'#fff', border:'1px solid '+(alert?'#F5B8B8':'var(--border-light)'),
      borderRadius:10, padding:'16px 20px', position:'relative'}}>
      {alert && (
        <div style={{position:'absolute',top:10,right:10,width:8,height:8,
          borderRadius:'50%',background:'#8F2E24'}}></div>
      )}
      <div style={{fontSize:9,fontWeight:600,textTransform:'uppercase',
        letterSpacing:'.1em',color:color||'#556B2F',marginBottom:8}}>{label}</div>
      <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'2rem',
        fontWeight:700,color:color||'#3B2A21'}}>{value}</div>
      {sub && <div style={{fontSize:11,color:'#8A8A8A',marginTop:4}}>{sub}</div>}
    </div>
  );

  return (
    <div>
      <div className="mb-lg">
        <h2 className="page-title">📊 Finance & Records Dashboard</h2>
        <p className="text-muted text-sm" style={{marginTop:4}}>
          Welcome, {user?.name}. Here's your expense recording summary.
        </p>
      </div>

      {/* Alert banner if issues exist */}
      {(parseInt(data.unrecorded?.count||0) > 0 ||
        parseInt(data.duplicates?.count||0) > 0 ||
        parseInt(data.missing_receipts?.count||0) > 0) && (
        <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',borderRadius:10,
          padding:'12px 16px',marginBottom:20,fontSize:13,color:'#8F2E24'}}>
          ⚠ <strong>Action required:</strong> You have items that need attention below.
        </div>
      )}

      {/* Stat tiles */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',
        gap:12,marginBottom:24}}>
        <StatTile label="Unrecorded Expenses" value={data.unrecorded?.count||0}
          sub="Paid bills without expense entry"
          color="#8F2E24" bg="#FDEAEA"
          alert={parseInt(data.unrecorded?.count||0) > 0} />
        <StatTile label="Missing Receipts" value={data.missing_receipts?.count||0}
          sub="Last 30 days"
          color="#8B5E0A" bg="#FFF3DC"
          alert={parseInt(data.missing_receipts?.count||0) > 0} />
        <StatTile label="Possible Duplicates" value={data.duplicates?.count||0}
          sub="Last 30 days"
          color="#8F2E24" bg="#FDEAEA"
          alert={parseInt(data.duplicates?.count||0) > 0} />
        <StatTile label="Unreconciled" value={data.unreconciled?.count||0}
          sub="This month"
          color="#1E4FAD" bg="#EDF4FF" />
        <StatTile label="Unpaid Bills" value={data.unpaid_bills?.count||0}
          sub={fmt(data.unpaid_bills?.total||0)+' outstanding'}
          color="#8B5E0A" bg="#FFF3DC"
          alert={parseInt(data.unpaid_bills?.count||0) > 0} />
        <StatTile label="This Week" value={fmt(data.week_expenses?.total||0)}
          sub={(data.week_expenses?.count||0)+' expenses'} />
        <StatTile label="This Month" value={fmt(data.month_expenses?.total||0)}
          sub={(data.month_expenses?.count||0)+' expenses'} />
      </div>

      {/* Two column layout */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>

        {/* Recent Expenses */}
        <div style={{background:'#fff',border:'1px solid var(--border-light)',
          borderRadius:10,overflow:'hidden'}}>
          <div style={{padding:'12px 16px',background:'#3B2A21',
            display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontSize:13,fontWeight:600,color:'#A8893D'}}>
              Recent Expenses
            </span>
            <a href="/expenses" style={{fontSize:11,color:'rgba(242,239,231,.6)'}}>
              View all →
            </a>
          </div>
          {(data.recent_expenses||[]).length === 0 ? (
            <div style={{padding:20,textAlign:'center',color:'#8A8A8A',fontSize:13}}>
              No recent expenses
            </div>
          ) : (data.recent_expenses||[]).map(e => (
            <div key={e.id} style={{padding:'10px 16px',
              borderBottom:'1px solid #F2EFE7',
              display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <div style={{fontSize:12,fontWeight:500,color:'#3B2A21'}}>{e.description}</div>
                <div style={{fontSize:10,color:'#8A8A8A'}}>
                  {e.category} · {e.date}
                  {!e.reference && (
                    <span style={{color:'#8F2E24',marginLeft:6}}>⚠ No receipt</span>
                  )}
                </div>
              </div>
              <div style={{fontSize:13,fontWeight:600,color:'#556B2F'}}>
                {fmt(e.amount)}
              </div>
            </div>
          ))}
        </div>

        {/* Unpaid Bills */}
        <div style={{background:'#fff',border:'1px solid var(--border-light)',
          borderRadius:10,overflow:'hidden'}}>
          <div style={{padding:'12px 16px',background:'#3B2A21',
            display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontSize:13,fontWeight:600,color:'#A8893D'}}>
              Unpaid Bills
            </span>
            <a href="/bills" style={{fontSize:11,color:'rgba(242,239,231,.6)'}}>
              View all →
            </a>
          </div>
          {(data.unpaid_bills_list||[]).length === 0 ? (
            <div style={{padding:20,textAlign:'center',color:'#8A8A8A',fontSize:13}}>
              No unpaid bills
            </div>
          ) : (data.unpaid_bills_list||[]).map(b => (
            <div key={b.id} style={{padding:'10px 16px',
              borderBottom:'1px solid #F2EFE7',
              display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <div style={{fontSize:12,fontWeight:500,color:'#3B2A21'}}>{b.description}</div>
                <div style={{fontSize:10,color:'#8A8A8A'}}>
                  {b.supplier_name} · Due: {b.due_date||'—'}
                </div>
              </div>
              <div style={{fontSize:13,fontWeight:600,color:'#8F2E24'}}>
                {fmt(b.balance_due)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick actions */}
      <div style={{marginTop:20,background:'#F2EFE7',borderRadius:10,
        padding:'16px 20px'}}>
        <div style={{fontSize:11,fontWeight:600,color:'#556B2F',
          textTransform:'uppercase',letterSpacing:'.08em',marginBottom:12}}>
          Quick Actions
        </div>
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          {[
            ['Record Expense', '/expenses'],
            ['View Bills', '/bills'],
            ['View Suppliers', '/suppliers'],
            ['Requisitions', '/requisitions'],
            ['Assets', '/assets'],
          ].map(([label, href]) => (
            <a key={label} href={href} className="btn btn-outline btn-sm"
              style={{fontSize:12}}>
              {label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
